import React from "react";
import { fetchAttendanceForSession, fetchMessagingThreads, fetchMyOpenings, fetchSessionsForCourse } from "../../../api/apiClient";
import useAsync from "../widgets/useAsync";
import { Bar, LoadFailed, Loading, fmtDate } from "../widgets/commonBodies";
import { ROUTES } from "../shared/classData";

const OPENING_WINDOW_DAYS = 45;
const iso = (d) => d.toISOString().slice(0, 10);
const initials = (name) => String(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

/** Learners checked in for a session that is done or running now (null when the session can't be matched). */
export async function presentCount(courseId, row, now) {
  const sessions = (await fetchSessionsForCourse(courseId)) || [];
  const day = iso(now);
  const match = sessions.find((s) => s.sessionDate === day && String(s.startTime || "").slice(0, 5) === row.start) || sessions.find((s) => s.sessionDate === day);
  if (!match) return null;
  const attendance = (await fetchAttendanceForSession(match.id)) || [];
  return attendance.filter((a) => a.attended || a.checkedInAt).length;
}

/** Today's sessions with check-in counts for the ones that have started. */
export function TodayBody({ ctx }) {
  const { today } = ctx.m;
  const started = today.filter((s) => s.state !== "next");
  const { data } = useAsync(async () => {
    const entries = await Promise.all(started.slice(0, 4).map(async (s) => [s.id, await presentCount(s.courseId, s, ctx.now).catch(() => null)]));
    return Object.fromEntries(entries);
  }, [started.map((s) => s.id).join(",")]);
  if (!today.length) return <p className="wg-empty">No sessions today.</p>;
  return (
    <div className="its" data-testid="instructor-today">{today.map((s, i) => {
      const present = data ? data[s.id] : undefined;
      return (
        <div key={s.id} className={`it ${s.state}`}>
          <div className="it-t"><b>{s.start}</b><small>{s.end}</small></div>
          <span className="it-bar" style={{ background: s.color }}></span>
          <div className="it-m"><b>{s.title}</b>
            <small>{[s.code && <span key="c" className="mo">{s.code}</span>, s.location, `${s.learners} enrolled`].filter(Boolean).reduce((acc, part, k) => (k ? [...acc, " · ", part] : [part]), [])}</small>
            {s.state !== "next" && present != null && s.learners > 0 && <div className="pbl" style={{ maxWidth: 260 }}><Bar pct={present / s.learners * 100} delay={i * 80} color={s.state === "live" ? "var(--color-accent-violet)" : "var(--color-ink)"} /><span>{present} of {s.learners} present</span></div>}</div>
          <div className="wr-btns">
            {s.state === "live" ? <span className="tag live">In session</span> : s.state === "done" ? <span className="tag ok">Complete</span> : <span className="tag mut">Starts {s.start}</span>}
            <button type="button" className="go" onClick={() => ctx.navigate(ROUTES.courseHub(s.courseId))}>{s.state === "live" ? "Take attendance" : s.state === "done" ? "Review" : "Roster"}</button>
          </div>
        </div>
      );
    })}</div>
  );
}

/** Upcoming sessions in my tenant that still need an instructor and that I could take. */
export function OpenBody({ ctx }) {
  const { loading, data, error } = useAsync(() => {
    const to = new Date(+ctx.now + OPENING_WINDOW_DAYS * 86400000);
    return fetchMyOpenings(ctx.tenantId, iso(ctx.now), iso(to));
  }, [ctx.tenantId]);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="open assignments" />;
  const rows = (data || []).filter((o) => o.status === "OPEN" && !o.assignedToMe && !o.mySignupStatus).slice(0, 6);
  if (!rows.length) return <p className="wg-empty">No open assignments right now.</p>;
  return (
    <div className="wl">{rows.map((o) => (
      <div key={o.openingId || o.id} className="wr"><div><b>{o.courseTitle || o.classTitle || o.sessionName || "Session"}</b>
        <small>{fmtDate(o.sessionDate, { weekday: "short", month: "short", day: "numeric" })}{o.startTime ? ` · ${String(o.startTime).slice(0, 5)}` : ""}{o.teachingRole ? ` · ${String(o.teachingRole).toLowerCase().replace(/_/g, " ")}` : ""}</small></div>
        <button type="button" className="go" onClick={() => ctx.navigate(ROUTES.instructorScheduling)}>Sign up</button></div>))}</div>
  );
}

/** Message threads with unread messages. */
export function MessagesBody({ ctx }) {
  const { loading, data, error } = useAsync(() => fetchMessagingThreads(), []);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="messages" />;
  const threads = Object.values(data?.threads || {}).filter((t) => t.unread > 0).slice(0, 5);
  if (!threads.length) return <p className="wg-empty">No unread messages.</p>;
  return (
    <div className="wl">{threads.map((t) => (
      <div key={t.id} className="mr u"><i className="av">{initials(t.title)}</i>
        <div><b>{t.title}</b><small>{[t.subtitle, `${t.unread} unread`].filter(Boolean).join(" · ")}</small></div></div>))}
      <button type="button" className="go" style={{ justifySelf: "start" }} onClick={() => ctx.navigate(ROUTES.messaging)}>Open inbox</button></div>
  );
}
